import { Injectable }            from '@angular/core';
import { UserGradeEnum }         from '../_utils/enums/user-grade.enum';
import { TranslateService }      from '@ngx-translate/core';
import { ModuleEnum }            from '../_utils/unums/module.enum';
import { UserJourneyService }    from './user-journey.service';
import { Storage }               from '@ionic/storage-angular';
import { lastValueFrom }         from 'rxjs';
import { QuestionService }       from './question.service';
import { UserAssessmentService } from './user-assessment.service';
import { SettingsService }       from './settings.service';
import { ModuleService }         from './module.service';

@Injectable({
  providedIn: 'root'
})
export class UserGradeService {
  private userGradeStrategy: any;

  constructor(
    private translateService: TranslateService,
    private userJourneyService: UserJourneyService,
    private storage: Storage,
    private questionService: QuestionService,
    private userAssessmentService: UserAssessmentService,
    private settingsService: SettingsService,
    private moduleService: ModuleService
  ) {
    this.initUserGradeStrategy();
  }

  public async userGradeStrategyHandler(module: ModuleEnum, key: string): Promise<any> {
    return await this.userGradeStrategy[key](module);
  }

  public async saveUserGrade(module: ModuleEnum | string, payload: { [key: string]: any }): Promise<void> {
    await this.userJourneyService.updateUserJourney(`modules/${module}`, payload);
  }

  public async hasUserGrade(module: ModuleEnum | string): Promise<boolean> {
    const userJourney = await this.storage.get('user_journey');
    const userModule = userJourney?.modules?.[module];
    return !!userModule?.grade;
  }

  private initUserGradeStrategy() {
    this.userGradeStrategy = {
      ['pre_training']: async (module: ModuleEnum) => {
        const settings = await this.getUserGradeSettings(module, 'pre_training', 'preTraining');
        const userAssessments = await this.getUserAssessments(module, 'pre_training');
        const userGrade = await this.generateUserGradeTraining(settings, userAssessments);
        await this.saveUserGrade(module, { grade: userGrade.grade, ['grade_avg']: userGrade.avg });
        return userGrade;
      },
      ['post_training']: async (module: ModuleEnum) => {
        const settings = await this.getUserGradeSettings(module, 'post_training', 'postTraining');
        const userAssessments = await this.getUserAssessments(module, 'post_training');
        const userGrade = await this.generateUserGradeTraining(settings, userAssessments);
        await this.saveUserGrade(module, { ['final_grade']: userGrade.grade, ['final_grade_avg']: userGrade.avg });
        return userGrade;
      }
    };
  }

  private async getUserGradeSettings(module: ModuleEnum, key: string, property: string): Promise<any> {
    const moduleProperty = await lastValueFrom(
      this.moduleService.getModulePropertyByKey(module, `settings.userGrade.${property}`)
    );
    const userGradeId = moduleProperty?.data;

    return userGradeId
      ? this.getUserGradeSettingsById(userGradeId)
      : this.getUserGradeSettingsByKey(module, key);
  }

  private async getUserGradeSettingsById(userGradeId: string): Promise<any> {
    const settings = await this.settingsService.getSettingById(userGradeId);
    return settings?.payload;
  }

  private async getUserGradeSettingsByKey(module: ModuleEnum, key: string): Promise<any> {
    const [settings] = await lastValueFrom(
      this.settingsService.getSettingByCategoryAndKey('USER_GRADE', `${module}/${key}`)
    );

    return settings?.payload;
  }

  private async generateUserGradeTraining(settings: any, userAssessments: any[]): Promise<any> {
    const generalAVG = this.getAverageBySection(settings, 'general', userAssessments);
    const groups = settings.groups.filter(x => x.key !== 'general');
    let avg = generalAVG;
    for (const group of groups) {
      avg += this.getAverageBySection(settings, group.key, userAssessments);
    }
    const globalAVG = avg / (groups.length + 1);
    const userQualification = generalAVG >= globalAVG ? generalAVG : globalAVG;
    const userGrade: any = {
      avg: globalAVG
    };
    if (userQualification >= 7) {
      userGrade.grade = UserGradeEnum.advance;
    } else if (userQualification >= 5) {
      userGrade.grade = UserGradeEnum.medium;
    } else {
      userGrade.grade = UserGradeEnum.beginner;
    }
    userGrade.content = settings.grades.find(x => x.key === userGrade.grade);
    userGrade.urlVideo = userGrade.content.urlVideo[this.translateService.currentLang];
    return userGrade;
  }

  private computeAverage(items: any[], base: number) {
    return base * items.reduce((prev, item) => prev + (item.value * item.weight), 0) / (100);
  }

  private getAverageBySection(settings: any, sectionKey: string, userAssessments: any[]) {
    const generalItems = settings.groups.find(x => x.key === sectionKey)?.items;
    const general = userAssessments.filter(x => generalItems.find(y => y === x.key));
    return this.computeAverage(general, 5 / 3);
  }

  private getUserAssessments = async (module: ModuleEnum, key: string): Promise<any[]> => {
    const user = await this.storage.get('user');
    const assessmentId = await this.getAssessmentId(module, key);
    const [questions, [userAssessment]] = await Promise.all([
      lastValueFrom(this.questionService.getQuestionsByAssessment(assessmentId)),
      lastValueFrom(this.userAssessmentService.getUserAssessment(user.uid, assessmentId, module)),
    ]);
    const result = [];
    for (const answer of userAssessment?.answers || []) {
      const question = questions.find(q => q.key === answer.questionId);
      result.push({
        key: answer.questionId,
        value: answer.answer,
        weight: question?.weight || 0
      });
    }
    return result;
  };

  private async getAssessmentId(module: ModuleEnum, key: string): Promise<string> {
    if (key === 'pre_training') {
      return await this.moduleService.getAssessmentIdByProperty(module, key, 'settings.assessments.preTraining');
    }
    if (key === 'post_training') {
      return await this.moduleService.getAssessmentIdByProperty(module, key, 'settings.assessments.postTraining');
    }
    return `${module}/${key}`;
  }
}
